import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _styled from '@emotion/styled-base';
import React__default, { forwardRef, useRef } from 'react';
import { Icon } from '../icon/icon.js';
import { Text } from '../text/text.js';
import { textDisabled, intentPrimaryActive, intentPrimary, intentPrimaryTranslucentActive, borderRadiusSmall, intentPrimaryHover, intentPrimaryTranslucentHover, textHeading, text } from '../theme/theme.js';
import useCombinedRefs from '../../utils/use-combined-refs.js';
import cx from 'classnames';

var _excluded = ["disabled", "hasDescription"],
    _excluded2 = ["active", "disabled", "hasDescription"],
    _excluded3 = ["active", "className", "description", "disabled", "href", "icon", "leftElement", "multiline", "onClick", "rightElement", "text"];

var NavigationItemContainer = /*#__PURE__*/_styled("a", {
  target: "ec9s7140"
})("display:flex;flex-direction:row;align-items:flex-start;width:100%;min-height:2.5rem;padding:0.5rem 0.5rem 0.5rem 0.75rem;color:", function (_ref) {
  var active = _ref.active,
      disabled = _ref.disabled;
  return disabled ? "".concat(textDisabled, " !important") : active ? intentPrimaryActive : intentPrimary;
}, ";background-color:", function (_ref2) {
  var active = _ref2.active,
      disabled = _ref2.disabled;
  return disabled ? "transparent" : active && intentPrimaryTranslucentActive;
}, ";border-radius:", borderRadiusSmall, ";&:hover{color:", function (_ref3) {
  var disabled = _ref3.disabled;
  return !disabled && intentPrimaryHover;
}, ";text-decoration:none;background-color:", function (_ref4) {
  var disabled = _ref4.disabled;
  return !disabled && intentPrimaryTranslucentHover;
}, ";cursor:", function (_ref5) {
  var disabled = _ref5.disabled;
  return disabled ? "not-allowed" : "pointer";
}, ";}&:active{color:", function (_ref6) {
  var disabled = _ref6.disabled;
  return !disabled && intentPrimaryActive;
}, ";text-decoration:none;background-color:", function (_ref7) {
  var disabled = _ref7.disabled;
  return !disabled && intentPrimaryTranslucentActive;
}, ";}&:hover,&:active{.vds-navigation-item-icon,.vds-navigation-item-text,.vds-navigation-item-description{color:inherit;}}.vds-navigation-item-right-element{pointer-events:", function (_ref8) {
  var disabled = _ref8.disabled;
  return disabled && "none";
}, ";}");

var NavigationItemIcon = /*#__PURE__*/_styled(function (_ref9) {
  _ref9.disabled;
      _ref9.hasDescription;
      var props = _objectWithoutProperties(_ref9, _excluded);

  return /*#__PURE__*/React__default.createElement(Icon, props);
}, {
  target: "ec9s7141"
})("margin:0.25rem 0.5rem 0 0;color:", function (_ref10) {
  var disabled = _ref10.disabled,
      hasDescription = _ref10.hasDescription;
  return disabled ? textDisabled : hasDescription && textHeading;
}, ";");

var NavigationItemText = /*#__PURE__*/_styled(function (_ref11) {
  _ref11.active;
      _ref11.disabled;
      _ref11.hasDescription;
      var props = _objectWithoutProperties(_ref11, _excluded2);

  return /*#__PURE__*/React__default.createElement(Text, props);
}, {
  target: "ec9s7142"
})("flex:1 1 auto;margin-top:0.125rem;color:", function (_ref12) {
  var active = _ref12.active,
      disabled = _ref12.disabled,
      hasDescription = _ref12.hasDescription;
  return disabled ? textDisabled : active ? intentPrimaryActive : hasDescription && textHeading;
}, ";font-weight:", function (_ref13) {
  var hasDescription = _ref13.hasDescription;
  return hasDescription && "600";
}, ";");

var NavigationItemDescription = /*#__PURE__*/_styled("div", {
  target: "ec9s7143"
})("margin-top:0.125rem;color:", function (_ref14) {
  var active = _ref14.active,
      disabled = _ref14.disabled;
  return disabled ? textDisabled : active ? intentPrimaryActive : text;
}, ";font-size:0.75rem;font-weight:400;line-height:1.3333;white-space:normal;");

var NavigationItemEdgeElement = /*#__PURE__*/_styled("div", {
  target: "ec9s7144"
})({
  name: "hz32yn",
  styles: "display:flex;flex-direction:row;align-items:center;min-height:1.5rem;margin:0 0.5rem;:first-child{margin-left:0;}:last-child{margin-right:0;}> *:not(:first-child){margin-left:0.25rem;}> button[aria-haspopup][id*=\"radix\"] + div{margin-left:0;}"
});

var NavigationItem = /*#__PURE__*/forwardRef(function (_ref15, ref) {
  var _ref15$active = _ref15.active,
      active = _ref15$active === void 0 ? false : _ref15$active,
      className = _ref15.className,
      description = _ref15.description,
      disabled = _ref15.disabled,
      href = _ref15.href,
      IconIncoming = _ref15.icon,
      leftElement = _ref15.leftElement,
      _ref15$multiline = _ref15.multiline,
      multiline = _ref15$multiline === void 0 ? true : _ref15$multiline,
      onClick = _ref15.onClick,
      rightElement = _ref15.rightElement,
      text = _ref15.text,
      props = _objectWithoutProperties(_ref15, _excluded3);

  var navigationItemLinkRef = useRef(null);
  var combinedRef = useCombinedRefs(navigationItemLinkRef, ref);
  return /*#__PURE__*/React__default.createElement(NavigationItemContainer, _extends({
    active: active,
    className: cx("vds-navigation-item", className),
    disabled: disabled,
    href: href,
    onClick: !disabled ? function (e) {
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
    } : undefined,
    onKeyDown: function onKeyDown(e) {
      if (e.key === "ArrowDown") {
        var _navigationItemLinkRe;

        e.preventDefault();
        var nextElement = (_navigationItemLinkRe = navigationItemLinkRef.current) === null || _navigationItemLinkRe === void 0 ? void 0 : _navigationItemLinkRe.nextSibling;
        nextElement && nextElement.focus();
      }

      if (e.key === "ArrowUp") {
        var _navigationItemLinkRe2;

        e.preventDefault();
        var prevElement = (_navigationItemLinkRe2 = navigationItemLinkRef.current) === null || _navigationItemLinkRe2 === void 0 ? void 0 : _navigationItemLinkRe2.previousSibling;
        prevElement && prevElement.focus();
      }

      if (onClick !== undefined && e.key === "Enter") {
        onClick(e);
      }
    },
    ref: combinedRef,
    tabIndex: !!onClick ? 0 : undefined
  }, props), leftElement && /*#__PURE__*/React__default.createElement(NavigationItemEdgeElement, {
    className: "vds-navigation-item-left-element"
  }, leftElement), IconIncoming && /*#__PURE__*/React__default.createElement(NavigationItemIcon, {
    className: "vds-navigation-item-icon",
    disabled: disabled,
    hasDescription: !!description
  }, /*#__PURE__*/React__default.createElement(IconIncoming, null)), /*#__PURE__*/React__default.createElement(NavigationItemText, {
    active: active,
    className: "vds-navigation-item-text",
    disabled: disabled,
    ellipsize: !multiline,
    hasDescription: !!description
  }, text !== null && text !== void 0 ? text : null, description && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, " "
  /* Truncated title attribute requires an extra space. */
  , /*#__PURE__*/React__default.createElement(NavigationItemDescription, {
    active: active,
    className: "vds-navigation-item-description",
    disabled: disabled
  }, description))), rightElement && /*#__PURE__*/React__default.createElement(NavigationItemEdgeElement, {
    className: "vds-navigation-item-right-element"
  }, rightElement));
});

export { NavigationItem };
//# sourceMappingURL=navigation-item.js.map
